import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTable } from "@/lib/store";
import { ROMAN, fmtDate, statusColor } from "@/lib/util";
import { Mark } from "@/components/Logo";

const Block = ({ label, children }) => (
  <section className="mt-7">
    <div className="kicker" style={{ fontSize: 9 }}>{label}</div>
    <div className="mt-2" style={{ borderTop: "1px solid var(--hairline)" }}>{children}</div>
  </section>
);

const Row = ({ left, right }) => (
  <div
    className="flex justify-between gap-6 py-2.5 text-[13px]"
    style={{ borderBottom: "1px solid var(--hairline-soft)" }}
  >
    <span>{left}</span>
    <span className="shrink-0 text-right" style={{ color: "var(--ink-mute)" }}>{right}</span>
  </div>
);

/** Printable single page: highlights, commitments, attendance, agenda. */
export const OnePager = ({ meeting }) => {
  const { year, highlights, commitments, activeMembers, memberById, settings } = useTable();
  const [prework, setPrework] = useState([]);

  useEffect(() => {
    api
      .get("/prework", { params: { meetingId: meeting.id } })
      .then((r) => setPrework(r.data))
      .catch(() => {});
  }, [meeting.id]);

  const hl = highlights.filter((h) => h.meetingId === meeting.id);
  const items = commitments.filter((c) => c.meetingId === meeting.id);
  const present = meeting.presentMemberIds || [];
  const agenda = prework.filter((p) => p.onAgenda);
  const host = memberById(meeting.deepDiveHostId);
  const scribe = memberById(meeting.scribeId);

  return (
    <div id="one-pager" className="print-only" data-testid="one-pager">
      <header style={{ borderBottom: "2px solid var(--brass-bright)", paddingBottom: 14 }}>
        <div className="flex items-end justify-between gap-6">
          <div>
            <div className="flex items-center gap-2">
              <Mark size={18} />
              <div className="kicker" style={{ fontSize: 9 }}>The Table · {year?.id}</div>
            </div>
            <div className="flex items-baseline gap-4 mt-2">
              <span className="roman" style={{ fontSize: 44 }}>{ROMAN[meeting.num - 1]}</span>
              <h1 className="serif" style={{ fontSize: 28, lineHeight: 1.1 }}>
                {meeting.title || "Untitled"}
              </h1>
            </div>
          </div>
          <div className="text-right text-[12px]" style={{ color: "var(--ink-mute)" }}>
            <div>{fmtDate(meeting.date)}</div>
            {meeting.time && <div>{meeting.time}</div>}
            {meeting.venue && <div>{meeting.venue}</div>}
            <div>{meeting.type}</div>
          </div>
        </div>
      </header>

      <div className="flex gap-6 mt-4 text-[12px]" style={{ color: "var(--ink-mute)" }}>
        {meeting.resourceSpeaker && <span>Resource — {meeting.resourceSpeaker}</span>}
        {host && <span>Deep dive — {host.name}</span>}
        {scribe && <span>Scribe — {scribe.name}</span>}
      </div>

      <Block label="Attendance">
        <div className="grid grid-cols-2 gap-x-8">
          {activeMembers.map((m) => (
            <Row
              key={m.id}
              left={m.name}
              right={present.includes(m.id) ? "Present" : "Absent"}
            />
          ))}
        </div>
      </Block>

      {(
        <Block label="On the agenda">
          {agenda.length === 0 ? (
            <p className="text-[13px] py-3" style={{ color: "var(--ink-mute)" }}>
              Nothing on the agenda. Pre-submissions shape it.
            </p>
          ) : (
            agenda.map((p) => (
              <Row
                key={p.id}
                left={`${memberById(p.memberId)?.name || "Member"} — ${p.topic}`}
                right={p.priority}
              />
            ))
          )}
        </Block>
      )}

      <Block label="Highlights">
        {hl.length === 0 ? (
          <p className="text-[13px] py-3" style={{ color: "var(--ink-mute)" }}>
            No highlights recorded.
          </p>
        ) : (
          hl.map((h) => (
            <article key={h.id} className="py-3" style={{ borderBottom: "1px solid var(--hairline-soft)" }}>
              <h3 className="serif text-[17px]">{h.title}</h3>
              <p className="text-[12.5px] mt-1.5 whitespace-pre-wrap" style={{ color: "var(--ink-soft)" }}>
                {h.body}
              </p>
            </article>
          ))
        )}
      </Block>

      <Block label="Commitments made">
        {items.length === 0 ? (
          <p className="text-[13px] py-3" style={{ color: "var(--ink-mute)" }}>
            No commitments entered.
          </p>
        ) : (
          items.map((c) => (
            <Row
              key={c.id}
              left={`${memberById(c.memberId)?.name || "Member"} — ${c.text}`}
              right={`${c.status}${c.dueDate ? ` · ${fmtDate(c.dueDate)}` : ""}`}
            />
          ))
        )}
      </Block>

      <footer
        className="kicker mt-8 pt-3"
        style={{ fontSize: 8, borderTop: "1px solid var(--hairline)" }}
      >
        {settings?.forumName || "The Table"} — what happens at the table, stays at the table
      </footer>
    </div>
  );
};

export default OnePager;
